import { useEffect } from "react";

/**
 * Unterbindet Browser-Autofill in der gesamten internen App: Jedes
 * `<input>`/`<textarea>` ohne explizites `autocomplete`-Attribut erhält
 * `autocomplete="off"`.
 *
 * Hintergrund: KMVS-Formularfelder tragen Kundendaten (Adresse, Bankverbindung,
 * Telefon, E-Mail …). Ohne diese Bremse bietet der Browser an, die *eigene*
 * Adresse/Bankverbindung des Mitarbeiters in Kundenfelder vorzuschlagen bzw.
 * fremde Kundendaten im Browser-Profil zu speichern – beides unerwünscht.
 *
 * Bewusst zentral statt pro Formular: erfasst neben den zentralen Eingabe-
 * Primitiven auch die vielen Fluent-`<Input>`-Dialoge (die in body-Portale
 * rendern) sowie künftige Formulare. Der Hook wird nur in der AppShell
 * (authentifizierte Routen) montiert – Login/2FA und die öffentlichen
 * Kundenformulare bleiben unberührt, dort ist Autofill gewollt.
 *
 * Ausnahmen: Passwortfelder (Passwort-Manager-Verhalten) und alles unterhalb
 * eines `[data-allow-autofill]`-Elements werden nicht angefasst; ein bereits
 * gesetztes `autocomplete` wird nie überschrieben.
 */
export function useDisableBrowserAutofill(): void {
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const harden = (el: Element) => {
      const tag = el.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA") return;
      if (el.hasAttribute("autocomplete")) return;
      if ((el as HTMLInputElement).type === "password") return;
      if (el.closest("[data-allow-autofill]")) return;
      el.setAttribute("autocomplete", "off");
    };

    const scan = (root: Element) => {
      harden(root);
      root.querySelectorAll("input, textarea").forEach(harden);
    };

    // Bestehende Felder einmal absichern …
    scan(document.body);

    // … und neu eingehängte (Dialoge, Popover, dynamisch gerenderte Formulare)
    // live nachziehen.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node as Element);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);
}

export default useDisableBrowserAutofill;
